import React, { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Register() {
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const state = location.state || {};
  const isTest = state.passkey_type === 'test';
  const passkey_id = state.passkey_id;

  const [form, setForm] = useState({ company_name: '', address: '', contact_person: '', username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState(null);

  if (!passkey_id) return <Navigate to="/login" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    if (!isTest && (!form.company_name || !form.address || !form.contact_person || !form.username || !form.password)) {
      setError('All fields are mandatory for user accounts.');
      setLoading(false); return;
    }
    try {
      const result = await register({ ...form, passkey_id });
      if (result.generated_username) {
        setCredentials({ username: result.generated_username, password: result.generated_password });
      } else {
        toast.success('Registration complete!');
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (credentials) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full border border-slate-200">
          <h2 className="text-xl font-black text-slate-900 text-center mb-2" style={{ fontFamily: 'Chivo' }}>Your Login Credentials</h2>
          <p className="text-xs text-slate-500 text-center mb-6">Save these. You'll need them for future logins.</p>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5 mb-6 space-y-3" data-testid="generated-credentials">
            <div>
              <Label className="text-[10px] font-bold uppercase text-blue-600">Username</Label>
              <div className="text-lg font-mono font-black text-blue-800 bg-white rounded px-3 py-2 border border-blue-200 select-all" data-testid="gen-username">{credentials.username}</div>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-blue-600">Password</Label>
              <div className="text-lg font-mono font-black text-blue-800 bg-white rounded px-3 py-2 border border-blue-200 select-all" data-testid="gen-password">{credentials.password}</div>
            </div>
          </div>
          <Button onClick={() => navigate('/dashboard')} className="w-full h-12 text-base font-bold rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="continue-dashboard">
            Continue to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full border border-slate-200">
        <h2 className="text-xl font-black text-slate-900 text-center mb-1" style={{ fontFamily: 'Chivo' }}>
          {isTest ? 'Test Registration' : 'Company Registration'}
        </h2>
        <p className="text-xs text-slate-500 text-center mb-6">
          {isTest ? 'Fields are optional. Skip to auto-generate test data.' : 'All fields are mandatory.'}
        </p>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">Company Name {!isTest && <span className="text-red-500">*</span>}</Label>
            <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder={isTest ? 'Optional (auto-generated)' : 'Company name'}
              required={!isTest} className="mt-1 h-10 rounded-lg" data-testid="reg-company-name" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">Address {!isTest && <span className="text-red-500">*</span>}</Label>
            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder={isTest ? 'Optional' : 'Company address'}
              required={!isTest} className="mt-1 h-10 rounded-lg" data-testid="reg-address" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">Contact Person {!isTest && <span className="text-red-500">*</span>}</Label>
            <Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} placeholder={isTest ? 'Optional' : 'Contact person'}
              required={!isTest} className="mt-1 h-10 rounded-lg" data-testid="reg-contact" />
          </div>
          {!isTest && (
            <>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Create Username <span className="text-red-500">*</span></Label>
                <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required className="mt-1 h-10 rounded-lg" data-testid="reg-username" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Create Password <span className="text-red-500">*</span></Label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required className="mt-1 h-10 rounded-lg" data-testid="reg-password" />
              </div>
            </>
          )}
          <Button type="submit" disabled={loading} className="w-full h-12 text-base font-bold rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="reg-submit">
            {loading ? 'Registering...' : isTest ? 'Start Test (Skip = Auto-generate)' : 'Register & Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
